"use client";

import { Info, AlertTriangle } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "../ui/toast";

export default function Toaster() {
  const { toasts } = useToast();

  const variantStyles: Record<
    string,
    {
      bg: string;
      icon: unknown;
      closeIconColor: string;
      textColor: string;
    }
  > = {
    success: {
      bg: "bg-[#17C3A51A] text-[#17C3A51A] border-[0.5px] border-[#17C3A5CC]",
      icon: (
        <svg
          stroke="#17C3A5"
          fill="#17C3A5"
          strokeWidth="0"
          viewBox="0 0 24 24"
          height="23px"
          width="23px"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.28 9.28a.75.75 0 0 0-1.06-1.06l-5.97 5.97-2.47-2.47a.75.75 0 0 0-1.06 1.06l3 3a.75.75 0 0 0 1.06 0l6.5-6.5Z"></path>
          <path d="M12 1c6.075 0 11 4.925 11 11s-4.925 11-11 11S1 18.075 1 12 5.925 1 12 1ZM2.5 12a9.5 9.5 0 0 0 9.5 9.5 9.5 9.5 0 0 0 9.5-9.5A9.5 9.5 0 0 0 12 2.5 9.5 9.5 0 0 0 2.5 12Z"></path>
        </svg>
      ),
      closeIconColor: "#17C3A5",
      textColor: "text-[#17C3A5]",
    },
    warning: {
      bg: "bg-[#F59E0B1A] text-[#F59E0B1A] border-[0.5px] border-[#F59E0BCC]",
      icon: (
        <AlertTriangle className="h-5 w-5 text-[#F59E0B]" strokeWidth={2.5} />
      ),
      closeIconColor: "#F59E0B",
      textColor: "text-[#F59E0B]",
    },
    error: {
      bg: "bg-[#DA2F581A] text-[#DA2F58CC] border-[0.5px] border-[#DA2F58CC]",
      icon: (
        <svg
          stroke="#DA2F58"
          fill="#DA2F58"
          strokeWidth="0"
          viewBox="0 0 24 24"
          height="23px"
          width="23px"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path fill="none" d="M0 0h24v24H0V0z"></path>
          <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"></path>
        </svg>
      ),
      closeIconColor: "#DA2F58",
      textColor: "text-[#DA2F58]",
    },
    info: {
      bg: "bg-[#3B82F61A] text-[#3B82F61A] border-[0.5px] border-[#3B82F6CC]",
      icon: <Info className="h-5 w-5 text-[#3B82F6]" strokeWidth={2.5} />,
      closeIconColor: "#3B82F6",
      textColor: "text-[#3B82F6]",
    },
    default: {
      bg: "bg-white border-[0.5px] border-gray-300",
      icon: <Info className="h-5 w-5 text-gray-600" strokeWidth={2.5} />,
      closeIconColor: "#4B5563",
      textColor: "text-gray-700",
    },
  };

  return (
    <ToastProvider>
      {toasts.map(function ({
        id,
        title,
        description,
        action,
        variant = "info",
        ...props
      }) {
        const { bg, icon, closeIconColor, textColor } =
          variantStyles[variant ?? "info"] || variantStyles.info;

        return (
          <Toast
            key={id}
            {...props}
            className={`
              group relative w-80 !min-h-[51px] max-w-sm
              rounded-xl p-0 overflow-hidden
              transition-all duration-500
              hover:scale-[1.02] hover:shadow-xl
              border border-white/20
              animate-toast-slide-in
              backdrop-blur-md ${bg}
            `}
          >
            <div className="relative z-10 px-5 py-4 flex items-center justify-between w-full">
              <div>
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 mt-0.5">{icon}</div>

                  <div className="flex-1 min-w-0 space-y-1">
                    {title && (
                      <ToastTitle className="text-sm font-semibold tracking-wide text-white">
                        {title}
                      </ToastTitle>
                    )}
                    {description && (
                      <ToastDescription
                        className={`text-sm ${textColor} leading-relaxed`}
                      >
                        {description}
                      </ToastDescription>
                    )}
                  </div>
                </div>
              </div>

              {action && <div className="mt-3 pl-11">{action}</div>}
              <ToastClose iconColor={closeIconColor}></ToastClose>
            </div>
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
